"use client";

import { CheckCircleIcon } from "@phosphor-icons/react/dist/ssr/CheckCircle";
import { TagIcon } from "@phosphor-icons/react/dist/ssr/Tag";

import { COUPON_AMOUNT_TYPE, COUPON_TYPE, useOwnerProfile } from "@up/data";

import { useCart } from "./CartContext";

import type { TCoupon } from "@up/data";

function isScholarship(coupon: TCoupon) {
  return (
    coupon.type === COUPON_TYPE.FULL_SCHOLARSHIP ||
    coupon.type === COUPON_TYPE.PARTIAL_SCHOLARSHIP
  );
}

function formatDiscount(coupon: TCoupon) {
  const label = isScholarship(coupon) ? "scholarship award" : "off";
  if (coupon.amountType === COUPON_AMOUNT_TYPE.PERCENTAGE) {
    return `${coupon.amount}% ${label}`;
  }
  return `$${(coupon.amount / 100).toFixed(2)} ${label}`;
}

export function AvailableCodes() {
  const ownerProfile = useOwnerProfile();
  const cart = useCart();

  const eligible = ownerProfile?.coupons?.filter((coupon) => {
    if (coupon.expiration && new Date(coupon.expiration) < new Date())
      return false;
    if (coupon.maxUses > 0 && (coupon.useCount ?? 0) >= coupon.maxUses)
      return false;
    return true;
  });

  if (!eligible?.length) {
    return null;
  }

  return (
    <div
      data-testid="available-codes"
      className="mb-2 rounded border border-lime-300 bg-lime-100"
    >
      <p className="pl-3 pt-3 mb-2 text-sm font-semibold">
        Codes available to you
      </p>
      <ul className="list-none m-0 p-0 mb-2 space-y-2">
        {eligible.map((coupon) => {
          const isApplied = cart.data?.coupon?.code === coupon.code;
          return (
            <li
              key={coupon.code}
              className="flex items-center gap-3 justify-between px-3 text-sm"
              data-testid="available-code-item"
            >
              <span className="flex items-center gap-2 font-semibold grow">
                <div className="flex justify-center" style={{ width: "20px" }}>
                  {isApplied ? (
                    <CheckCircleIcon
                      size={20}
                      weight="fill"
                      className="text-green-600"
                    />
                  ) : (
                    <TagIcon size={16} weight="fill" />
                  )}
                </div>
                <span>{formatDiscount(coupon)}</span>
              </span>
              <span className="font-mono text-xs opacity-60">
                {coupon.code}
              </span>
              <button
                className={`checkout-button shrink-0 rounded border border-solid border-gray-400 bg-white py-0.5 px-4 disabled:opacity-50 disabled:cursor-not-allowed ${isApplied ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={() => cart.applyCoupon(coupon.code)}
                disabled={isApplied}
                type="button"
              >
                Apply
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
